import { Stack, Typography } from '@ethings/ui-toolkit';
import { FC, Suspense, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { LocaleSelect, MiddleSpinner, PhoneInput, TextEditor, TimeZoneSelect } from '../../components';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';
import { NotificationRecipientRadioButtons } from '../subscription/notification-recipient-radio-buttons';
import { RecipientAutocomplete } from '../subscription/recipient-autocomplete';
import { ContactMethodRadioButtons, DefaultCustomRadioButtons, NotificationGroupRadioButtons, NotificationRecipientViewProps, RadioButtons, UserRadioButtons } from '../subscription/subscription-detail.types';

export const CreateSubscriptionNotificationRecipientView: FC<NotificationRecipientViewProps> = ({
  channelDetailHelper,
  contactDetail,
  contactOptions,
  currentUser,
  defaultOptions,
  fieldDisabled,
  handleEmailFieldChange,
  handleSelectChange,
  isChannelDetailInvalid,
  isContactDetailFieldVisible,
  isContactOptionsVisible,
  isProfilePage,
  notificationGroupOptions,
  recipients,
  recipientsError,
  recipientsPending,
  recipientState,
  selectedLocale,
  selectedTimezone,
  setChannelDetailHelper,
  setContactDetail,
  setIsChannelDetailInvalid,
  setRecipientState,
  setSelectedLocale,
  setSelectedTimezone,
  userOptions,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  const titleText = t('admin:page.create-subscription.notification-recipient.labels.heading');
  const h5Styles = useResponsiveText('1.5rem', innerText(titleText).length, {
    minFontSize: '1.25rem',
    maxFontSize: '1.5rem',
    textLengthThreshold: 25,
  });

  const userOrOtherRadioButtons = (
    <NotificationRecipientRadioButtons
      data-testid="user-of-system-or-other"
      fieldDisabled={fieldDisabled || isProfilePage}
      heading={t('admin:page.create-subscription.notification-recipient.labels.update-user')}
      options={userOptions}
      selected={recipientState.user}
      setSelected={(user) => {
        setContactDetail(null);
        setRecipientState({
          ...recipientState,
          user: user as UserRadioButtons,
        });
      }}
    />
  );

  const smsOrEmailRadioButtons = (
    <NotificationRecipientRadioButtons
      data-testid="send-notification-via"
      fieldDisabled={fieldDisabled || contactOptions.length === 1}
      heading={t('admin:page.create-subscription.notification-recipient.labels.send-notification-via')}
      options={contactOptions}
      selected={recipientState.contactMethod}
      setSelected={(contactMethod) => {
        setChannelDetailHelper('');
        setIsChannelDetailInvalid(false);
        setContactDetail(null);
        setRecipientState({ ...recipientState, contactMethod: contactMethod as ContactMethodRadioButtons });
      }}
    />
  );

  const individualOrGroupRadioButtons = (
    <NotificationRecipientRadioButtons
      data-testid="send-notification-to"
      fieldDisabled={fieldDisabled || isProfilePage}
      heading={t('admin:page.create-subscription.notification-recipient.labels.send-notification')}
      options={notificationGroupOptions}
      selected={recipientState.notificationGroup}
      setSelected={(notificationGroup) => {
        setRecipientState({
          ...recipientState,
          notificationGroup: notificationGroup as NotificationGroupRadioButtons,
          selectedRecipient: null,
        });
      }}
    />
  );

  const defaultOrCustomRadioButtons = (
    <NotificationRecipientRadioButtons
      data-testid="default-or-custom-channel"
      fieldDisabled={fieldDisabled}
      options={defaultOptions}
      selected={recipientState.defaultOrCustomContact}
      setSelected={(defaultOption) => {
        if (defaultOption === RadioButtons.DEFAULT) {
          setContactDetail(null);
          setChannelDetailHelper('');
          setIsChannelDetailInvalid(false);
        }
        setRecipientState({ ...recipientState, defaultOrCustomContact: defaultOption as DefaultCustomRadioButtons });
      }}
    />
  );

  const renderEntitySelector = (): JSX.Element => {
    if (recipientsPending) return <MiddleSpinner />;
    if (recipientsError) return <Typography>{t('common:page.subscription-detail.error.hint')}</Typography>;
    const initialSelection = isProfilePage ? currentUser : undefined;

    return (
      <RecipientAutocomplete
        key={recipientState.notificationGroup}
        disabled={fieldDisabled || isProfilePage}
        initialSelection={initialSelection}
        handleSelect={(value) => {
          if (!value) return;
          if (recipientState.notificationGroup === RadioButtons.INDIVIDUAL) {
            setContactDetail(null);
            handleSelectChange('defaultOrCustomContact', RadioButtons.DEFAULT);
            setChannelDetailHelper('');
            setIsChannelDetailInvalid(false);
          }
          handleSelectChange('selectedRecipient', value._id);
        }}
        recipients={recipients}
      />
    );
  };

  const emailDetailField = (
    <TextEditor
      disabled={fieldDisabled || (recipientState.user !== RadioButtons.OTHER && recipientState.defaultOrCustomContact === RadioButtons.DEFAULT)}
      error={isChannelDetailInvalid}
      helperText={channelDetailHelper}
      label={t('admin:page.create-subscription.notification-recipient.labels.email-address')}
      onKeyUp={(value) => handleEmailFieldChange(value)}
      placeholder={t('admin:page.create-user.basic-info.email.hint')}
      required
      size="small"
      value={contactDetail ?? ''}
      data-testid='email-address-input'
    />
  );

  const phoneDetailField = (
    <PhoneInput
      disabled={fieldDisabled || (recipientState.user !== RadioButtons.OTHER && recipientState.defaultOrCustomContact === RadioButtons.DEFAULT)}
      disableFormatting
      label={t('admin:page.create-subscription.notification-recipient.labels.mobile-number')}
      onChange={(value) => setContactDetail(value)}
      size="small"
      mobileOnly={true}
      value={contactDetail ?? ''}
      data-testid='notification-sms-input'
    />
  );

  const timezoneAndLocale = <>
    <Typography variant="subtitle2">
      {t('admin:page.create-subscription.notification-recipient.labels.select-locale-time-zone')}
    </Typography>
    <Suspense fallback={<MiddleSpinner />}>
      <TimeZoneSelect
        fieldDisabled={fieldDisabled}
        selectedTimeZone={selectedTimezone}
        setSelectedTimeZone={setSelectedTimezone}
        data-testid='timezone-select'
      />
      <LocaleSelect
        fieldDisabled={fieldDisabled}
        selectedLocale={selectedLocale}
        setSelectedLocale={setSelectedLocale}
        data-testid='locale-select'
      />
    </Suspense>
  </>;

  const header = (
    <Typography
      variant="h5"
      style={{
        fontSize: h5Styles.fontSize,
        lineHeight: h5Styles.lineHeight,
        wordBreak: h5Styles.wordBreak,
        whiteSpace: h5Styles.whiteSpace,
        overflow: h5Styles.overflow,
        textOverflow: h5Styles.textOverflow,
      }}
    >
      {titleText}
    </Typography>
  );

  if (recipientState.user === RadioButtons.OTHER) {
    return (
      <Stack spacing={2}>
        {header}
        {userOrOtherRadioButtons}
        {smsOrEmailRadioButtons}
        {recipientState.contactMethod === RadioButtons.SMS ? phoneDetailField : emailDetailField}
        {timezoneAndLocale}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {header}
      {userOrOtherRadioButtons}
      {individualOrGroupRadioButtons}
      {renderEntitySelector()}
      {isContactOptionsVisible
        && <Stack>
          {smsOrEmailRadioButtons}
          {isContactDetailFieldVisible
            && <>
              {defaultOrCustomRadioButtons}
              {recipientState.contactMethod === RadioButtons.SMS ? phoneDetailField : emailDetailField}
            </>
          }
        </Stack>
      }
    </Stack>
  );
};
